import React, {ReactElement} from "react";
import {Box, Button, Card, Typography} from "@mui/material";
import {DIVIDER, PD_LG, PD_MD, PD_SM, SZ_LG, SZ_MD, SZ_SM, SZ_SMD} from "../../shared/dimens";
import {StyledBoxColumn, StyledBoxRow, StyledSpan} from "../../shared/StyledComponents";
import {GameConfig, GameData, GameOverResult} from "./types";
import {getAuth} from "@firebase/auth";
import {Game} from "../types";
import {
  FullscreenDialogWithTitleFragment,
  FullscreenDialogWithTitleFragmentProps,
  FullscreenDialogWithTitleFragmentState
} from "../../shared/FullscreenDialogWithTitleFragment";
import {BaseApp} from "../../shared/BaseApp";
import {FullscreenExitOutlined} from "@mui/icons-material";

export interface GameFragmentListener {

  onGameReset();

  onGameEnded();

  onBoardUpdated();

  onGameOver(result: GameOverResult, ...args);

  onStatusChanged(status: string);

}

export type GameFragmentProps = FullscreenDialogWithTitleFragmentProps & {
  gameFragmentListener?: GameFragmentListener,
}

export enum DisplayMode {
  DEFAULT = "default",
  IMMERSIVE = "immersive",
}

export type GameFragmentState<GC extends GameConfig, GD extends GameData<any>> =
  FullscreenDialogWithTitleFragmentState
  & {
  displayMode: DisplayMode,
  gameConfig?: GC,
  gameData?: GD,
}

export abstract class GameFragment<P extends GameFragmentProps, GC extends GameConfig, GD extends GameData<any>, S extends GameFragmentState<GC, GD>> extends FullscreenDialogWithTitleFragment<P, S> {

  protected readonly auth = getAuth();

  protected onCreateState(props?: P): S {
    return {
      ...super.onCreateState(props),
      displayMode: DisplayMode.DEFAULT,
    };
  }

  getTitle(): string {
    return this.getGame().name;
  }

  protected abstract getGame(): Game;

  render() {
    if (!this.state.gameConfig) {
      return this.renderConfigContent();
    }
    if (!this.state.gameData) {
      return this.renderPleaseWait();
    }
    if (this.state.displayMode === DisplayMode.IMMERSIVE) {
      return this.renderGameContentContainer();
    }
    return super.render();
  }

  protected finishGame(result: GameOverResult) {
    this.state.gameData.gamePlay.finish();
    this.props.gameFragmentListener?.onGameOver(result);
  }

  componentDidUpdate(prevProps: Readonly<P>, prevState: Readonly<S>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    if (prevState.gameConfig !== this.state.gameConfig) {
      this.reload(true);
    }
  }

  renderContent(): React.ReactElement {
    return <Box style={{display: "flex", flexDirection: "column", flexGrow: 1, position: "relative"}}>
      {this.renderGameToolbar()}
      {this.renderGameContentContainer()}
    </Box>;
  }

  private renderGameContentContainer(): ReactElement {
    return <div style={{
      position: "relative",
      width: "100%",
      display: "flex",
      flexBasis: 0,
      flexGrow: 1,
      overflowY: "hidden",
      height: this.state.displayMode === DisplayMode.IMMERSIVE ? "100dvh" : null,
    }}>
      {this.renderGameContent()}
      {this.state.displayMode === DisplayMode.IMMERSIVE
        ? <Card id={"exit"} style={{position: "absolute", right: 24, top: 24, zIndex: 9999}}>
          <Button
            style={{width: SZ_SMD, height: SZ_SMD}}
            onClick={() => this.setState({displayMode: DisplayMode.DEFAULT})}>
            <FullscreenExitOutlined/>
          </Button>
        </Card>
        : null}
    </div>
  }

  protected abstract renderConfigContent(): ReactElement;

  protected renderConfigContentForGame(game: Game, configOptions: ReactElement) {
    return <Box className="footer-tab-fragment-header"
                style={{
                  display: "flex",
                  width: "100%",
                  height: "100dvh",
                  flexDirection: "column",
                  position: "relative",
                  paddingBottom: SZ_LG,
                  background: game.color,
                }}>
      <Box style={{
        display: "flex",
        flexShrink: 0,
        height: SZ_MD,
        alignItems: "center",
        paddingLeft: PD_MD,
        paddingRight: PD_MD,
        gap: PD_MD,
        left: 0,
        right: 0,
      }}>
        <Button variant="outlined" style={{zIndex: 10000}} onClick={() => BaseApp.CONTEXT.hideDialog()}>Back</Button>
      </Box>
      <StyledBoxColumn
        style={{flexGrow: 1, alignItems: "center", justifyContent: "center", gap: PD_LG, marginTop: -SZ_MD}}>
        <img src={game.coverUrl} style={{width: 480, aspectRatio: 2}}/>
        <Typography variant="h4">{game.name}</Typography>
        <Typography style={{padding: PD_MD, textAlign: "center"}}>{game.description}</Typography>
        <StyledBoxColumn style={{width: 240}}>
          {configOptions}
          {!this.auth.currentUser && !BaseApp.CONTEXT.getAppConfig().loginConfig?.noLogin ?
            <Button variant="outlined" onClick={() => {
            }}>Log in</Button>
            : null}
        </StyledBoxColumn>
      </StyledBoxColumn>
    </Box>
  }

  private renderGameToolbar() {
    return <StyledBoxRow
      style={{position: "relative", padding: PD_SM, height: SZ_SM, borderBottom: DIVIDER, alignItems: "center"}}>
      {this.renderToolbarTitle()}
      <StyledSpan/>
      {this.renderToolbarButtons()}
    </StyledBoxRow>
  }

  protected renderToolbarTitle(): ReactElement {
    return null;
  }

  protected renderToolbarButtons(): ReactElement {
    return null;
  }

  protected abstract renderGameContent(): ReactElement;
}